import React, { useState, useEffect, useMemo } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
  TextField,
  Typography,
  Divider,
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Button
} from "@mui/material";
import "./ATMServices.scss";
import { getAllATMData } from "../../util/API/atm";
import { commonRunQuery } from "../../util/API/loans";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush } from 'recharts';
import RefreshIcon from '@mui/icons-material/Refresh';

const MUIStyledTableRow = ({ row, level = 0 }) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const isHighlight = (key, value) => {
    return (
      (key === "Bills Remaining" || key === "Value Remaining") && value > 0
    );
  };

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: "#FFFFFF",
          transition: "background-color 0.3s ease",
          "&:hover": { backgroundColor: "#F9F9F9" },
        }}
      >
        <TableCell
          sx={{
            padding: "12px",
            color: "#4F4F4F",
            borderBottom: "1px solid #E0E0E0",
            textAlign: "left",
          }}
        >
          <Box sx={{ paddingLeft: `${level * 20}px` }}>
            {" "}
            {/* Indenting the text */}
            {row.name}
          </Box>
        </TableCell>
        <TableCell
          sx={{
            padding: "12px",
            color: "#4F4F4F",
            borderBottom: "1px solid #E0E0E0",
            textAlign: "left",
          }}
        >
          <Box sx={{ paddingLeft: `${level * 20}px` }}>
            {" "}
            {/* Indenting the text */}
            {row.details}
          </Box>
        </TableCell>
        <TableCell
          sx={{
            textAlign: "left",
            padding: "20px",
            borderBottom: "1px solid #E0E0E0",
            width: "10%",
          }}
        >
          {row.children && (
            <IconButton
              className="expand-button"
              onClick={toggleExpand}
              sx={{ padding: "0", color: "#4F4F4F" }}
            >
              {expanded ? (
                <VisibilityOff className="keyboard-arrow-icon" />
              ) : (
                <Visibility className="keyboard-arrow-icon" />
              )}
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      {expanded &&
        row.children &&
        row.children.map((childRow, index) => (
          <React.Fragment key={index}>
            {childRow.isTable ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ paddingBottom: 0, paddingTop: 0 }}>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box
                      sx={{ margin: 1, paddingLeft: `${(level + 1) * 20}px` }}
                    >
                      <Table
                        size="small"
                        aria-label="child table"
                        sx={{
                          width: "100%",
                          borderCollapse: "collapse",
                          background: "#FFFFFF",
                          borderRadius: "0px 12px 12px 12px",
                          marginTop: "10px",
                          boxShadow: "0px 1px 15px rgba(0, 0, 0, 0.04)",
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            {Object.keys(childRow.data[0]).map((key, i) => (
                              <TableCell
                                key={i}
                                sx={{
                                  fontWeight: "bold",
                                  color: "#4F4F4F",
                                  padding: "12px",
                                  borderBottom: "1px solid #E0E0E0",
                                  textAlign: "center",
                                  font: "Nunito",
                                }}
                              >
                                {key}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {childRow.data.map((dataRow, i) => (
                            <TableRow
                              key={i}
                              sx={{
                                backgroundColor:
                                  i % 2 === 0 ? "#FFFFFF" : "#F9F9F9",
                              }}
                            >
                              {Object.entries(dataRow).map(
                                ([key, value], j) => (
                                  <TableCell
                                    key={j}
                                    sx={{
                                      padding: "12px",
                                      color: isHighlight(key, value)
                                        ? "#EB5757"
                                        : "#4F4F4F",
                                      backgroundColor: isHighlight(key, value)
                                        ? "#FFEAEA"
                                        : "inherit",
                                      fontWeight: isHighlight(key, value)
                                        ? "bold"
                                        : "normal",
                                      textAlign: "center",
                                      font: "Nunito",
                                    }}
                                  >
                                    {value}
                                  </TableCell>
                                )
                              )}
                            </TableRow>
                          ))}

                          <TableRow
                            sx={{
                              backgroundColor: "#F9F9F9",
                              fontWeight: "bold",
                            }}
                          >
                            {Object.keys(childRow.data[0]).map((key, index) => {
                              if (index === 0) {
                                // For the first cell (e.g., "Terminal ID"), add "Total"
                                return (
                                  <TableCell
                                    key={index}
                                    sx={{
                                      fontWeight: "bold",
                                      textAlign: "center",
                                      font: "Nunito",
                                    }}
                                  >
                                    Total
                                  </TableCell>
                                );
                              } 
                              else if (index < 4) {
                                return (
                                  <TableCell
                                    key={index}
                                    sx={{
                                      fontWeight: "bold",
                                      textAlign: "center",
                                      font: "Nunito",
                                    }}
                                  />
                                );
                              }
                              else {
                                // Calculate the sum for numeric fields and display an empty cell for non-numeric fields
                                const sum = childRow.data.reduce((acc, row) => {
                                  const value = parseFloat(row[key]);
                                  return acc + (isNaN(value) ? 0 : value);
                                }, 0);
                                return (
                                  <TableCell
                                    key={index}
                                    sx={{
                                      fontWeight: "bold",
                                      textAlign: "center",
                                      font: "Nunito",
                                    }}
                                  >
                                    {sum}
                                  </TableCell>
                                );
                              }
                            })}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            ) : (
              <MUIStyledTableRow row={childRow} level={level + 1} />
            )}
          </React.Fragment>
        ))}
    </>
  );
};

const StatusItem = React.memo(({ label, value }) => {
  const val = String(value).toLowerCase();
  
  // Logic for color coding
  const isError = (val.includes("error") && !val.includes("no error")) || val.includes("out") || val.includes("fail");
  const isNeutral = val.includes("not configured") || val.includes("inactive");
  const statusColor = isNeutral ? "#828282" : isError ? "#EB5757" : "#27AE60";

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', // Align dots with text baseline
        py: 1.5, 
        px: 2,
        mx: -2, // Offset Accordion padding to make hover full-width
        transition: "background-color 0.2s ease",
        cursor: "default",
        "&:hover": {
          backgroundColor: "#F4F7F9", // Light blue-tinted highlight on hover
        },
        "&:nth-of-type(odd)": {
          backgroundColor: "#FAFAFA", // Zebra striping for better tracking
        }
      }}
    >
      {/* 1. The Label */}
      <Typography sx={{ fontSize: '11px', color: '#4F4F4F', fontWeight: 700, whiteSpace: 'nowrap' }}>
        {label.replace(/([A-Z])/g, ' $1').toUpperCase()}
      </Typography>

      {/* 2. The Connecting Dots (The Bridge) */}
      <Box sx={{ 
        flexGrow: 1, 
        borderBottom: '1px dotted #E0E0E0', 
        mx: 2, 
        mb: '4px' // Adjust to align dots vertically with text center
      }} />

      {/* 3. The Value */}
      <Typography sx={{ fontSize: '11px', fontWeight: 800, color: statusColor, whiteSpace: 'nowrap' }}>
        {value.toUpperCase()}
      </Typography>
    </Box>
  );
});

const HardwareStatusRenderer = ({ specificATMStatus, expandedPanel, setExpandedPanel }) => {
  // 1. Move hook to the top (before any returns)
  const sections = useMemo(() => {
    // Handle the null check inside the hook logic
    if (!specificATMStatus) return []; 
    
    return [
      { title: "Hardware Fitness", data: specificATMStatus.hardware, icon: "⚙️", id: "hardware" },
      { title: "Supplies & Bins", data: specificATMStatus.supplies, icon: "📄", id: "supplies" },
      { title: "Sensors & Security", data: specificATMStatus.sensors, icon: "🛡️", id: "sensors" }
    ];
  }, [specificATMStatus]);

  // 2. Standard function handlers
  const handleChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  // 3. Early return for UI comes AFTER hooks
  if (!specificATMStatus) return null;

  return (
    <Box>
      {sections.map((section) => (
        <Accordion 
          key={section.id}
          expanded={expandedPanel === section.id} 
          onChange={handleChange(section.id)}
          disableElevation
          sx={{ mb: 1, borderRadius: "12px !important", border: "1px solid #EEE", '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
             <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
              {section.icon} {section.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 2, overflow: 'hidden' }}>
            {/* Ensure section.data exists before calling Object.entries */}
            {section.data && Object.entries(section.data).map(([key, val]) => (
              <StatusItem key={key} label={key} value={val} />
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

const ATMDetailView = ({ 
  atm, onBack, heartbeatHistory, chartHistory, specificATMStatus, 
  currentPage, setCurrentPage, selectedDate, setSelectedDate, 
  expandedPanel, setExpandedPanel, onRefresh
}) => {
const entriesPerPage = 5;

const specificHistory = useMemo(() => {
  return heartbeatHistory.filter((h) => h.atmrecid == atm.recid || h.device === atm.atmid);
}, [heartbeatHistory, atm.recid, atm.atmid]);

const chartData = useMemo(() => {
  // Use chartHistory instead of specificHistory
  const formattedData = chartHistory
    .map((h) => ({
      time: h.rectime.split(' ')[1] || h.rectime,
      httpsLat: Number(h.corelatency) || 0,
      ipLat: Number(h.switchlatency) || 0,
      failCount: Number(h.failures) || 0,
    }))
    .sort((a, b) => a.time.localeCompare(b.time));

  const displayData = formattedData.length > 500 
    ? formattedData.filter((_, index) => index % 10 === 0) 
    : formattedData;

  return displayData;
}, [chartHistory]);

const currentEntries = specificHistory.slice(0, currentPage * entriesPerPage);
const hasMore = (currentPage * entriesPerPage) < specificHistory.length;

return (
  <Box>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={onBack} sx={{ mr: 1, color: "#00365A" }}>
            <ArrowBackIosNewIcon sx={{ fontSize: "18px" }}/>
          </IconButton>
          <Box>
            <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#00365A" }}>{atm.name}</Typography>
            <Typography sx={{ fontSize: "12px", color: "#828282" }}>System ID: {atm.atmid}</Typography>
          </Box>
        </Box>

        {/* Add Refresh Button Here */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          sx={{ 
            color: "#00365A", 
            borderColor: "#00365A",
            textTransform: "none",
            borderRadius: "8px",
            height: "fit-content"
          }}
        >
          Refresh Data
        </Button>
      </Box>

    <Box sx={{ mb: 4, p: 3, bgcolor: "#F8F9FA", borderRadius: "12px", border: "1px solid #EEE" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#00365A" }}>LATENCY ANALYTICS</Typography>
        <TextField type="date" size="small" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} sx={{ bgcolor: "#fff" }} />
      </Box>

      {chartData.length > 0 ? (
        <Box sx={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E0E0E0" />
            <XAxis dataKey="time" fontSize={10} />
            
            {/* Left YAxis with Label */}
            <YAxis 
              yAxisId="left" 
              fontSize={10} 
              domain={[-5, 'auto']} 
              ticks={[-5, 0, 500, 1000, 1500]}
              label={{ 
                value: 'Latency (ms)', 
                angle: -90, 
                position: 'insideLeft', 
                style: { textAnchor: 'middle', fontSize: 12, fill: '#666' } 
              }} 
            />
            
            {/* Right YAxis with Label */}
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              fontSize={10}
              label={{ 
                value: 'Failures', 
                angle: 90, 
                position: 'insideRight', 
                style: { textAnchor: 'middle', fontSize: 12, fill: '#666' } 
              }} 
            />
            
            <Tooltip />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="httpsLat" name="Core (ms)" stroke="#00365A" strokeWidth={2.5} dot={false} isAnimationActive={false} />
            <Line yAxisId="left" type="monotone" dataKey="ipLat" name="Switch (ms)" stroke="#27AE60" strokeWidth={2.5} dot={false} isAnimationActive={false} />
            <Line yAxisId="right" type="step" dataKey="failCount" name="Failures" stroke="red" strokeDasharray="4 4" isAnimationActive={false} />
            <Brush dataKey="time" height={30} stroke="#00365A" />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      ) : (
        <Typography sx={{ fontSize: "12px", color: "#828282", textAlign: "center", py: 8 }}>No data for {selectedDate}</Typography>
      )}
    </Box>

    <Typography sx={{ fontSize: "13px", fontWeight: 600, mb: 1, color: "#4F4F4F" }}>LATEST HARDWARE REPORT</Typography>
    <HardwareStatusRenderer specificATMStatus={specificATMStatus} expandedPanel={expandedPanel} setExpandedPanel={setExpandedPanel} />

    <Divider sx={{ my: 3 }} />

    <Typography sx={{ fontSize: "13px", fontWeight: 600, mb: 2, color: "#4F4F4F" }}>LATENCY HISTORY</Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    {currentEntries.map((hb) => (
      <Box 
        key={hb.recid} 
        sx={{ 
          p: 1.5, 
          borderRadius: "8px", 
          border: "1px solid #eee", 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", // Vertically center the content
          bgcolor: "#fff",
          mb: 1 // Add spacing between list items
        }}
      >
        <Box>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "#00365A" }}>{hb.device}</div>
          <div style={{ fontSize: "10px", color: "#828282" }}>{hb.rectime}</div>
        </Box>

        <Box sx={{ display: "flex", gap: 3, textAlign: "right" }}>
          {/* Switch Latency Section */}
          <Box>
            <div style={{ 
              fontSize: "12px", 
              fontWeight: 700, 
              color: hb.switchlatency > 500 ? "#EB5757" : "#27AE60" 
            }}>
              {hb.switchlatency}ms
            </div>
            <div style={{ fontSize: "9px", color: "#828282", fontWeight: 700 }}>SWITCH</div>
          </Box>

          {/* Core Latency Section */}
          <Box>
            <div style={{ 
              fontSize: "12px", 
              fontWeight: 700, 
              color: hb.corelatency > 500 ? "#EB5757" : "#27AE60" 
            }}>
              {hb.corelatency}ms
            </div>
            <div style={{ fontSize: "9px", color: "#828282", fontWeight: 700 }}>CORE</div>
          </Box>
        </Box>
      </Box>
    ))}
      {hasMore && (
        <IconButton onClick={() => setCurrentPage(p => p + 1)} sx={{ border: "1px solid #00365A", color: "#00365A", fontSize: "13px", py: 1, borderRadius: "8px", mb:4 }}>
          Load More
        </IconButton>
      )}
    </Box>
  </Box>
);
};

const ATMServices = () => {
  const [data, setData] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [liveStatus, setLiveStatus] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [heartbeatHistory, setHeartbeatHistory] = useState([]);
  const [latestSystemStatus, setLatestSystemStatus] = useState(null);
  const [liveSubTab, setLiveSubTab] = useState("list"); // "list", "history", or "global"
  const [selectedATM, setSelectedATM] = useState(null);
  const [specificATMStatus, setSpecificATMStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [expandedPanel, setExpandedPanel] = useState(false);
  const [chartHistory, setChartHistory] = useState([]);

  useEffect(() => {
    async function fetchATMData() {
      try {
        const response = await getAllATMData();

        if (!response || response.Status !== "1" || !Array.isArray(response.payload)) {
          console.error("Invalid ATM data response:", response);
          return;
        }

        let allBillInventoryData = [];
        let allSystemStatusData = [];

        response.payload.forEach((atm) => {
          const atmid = atm.atmid;
          const atmStatus = atm.atmstatus;
          const cassetteNotes = atm.cassette_notes;

          if (cassetteNotes) {
            allBillInventoryData.push(...processResponseData(cassetteNotes));
          }

          if (atmStatus?.reportdata) {
            try {
              const cleanData = atmStatus.reportdata.replace(/&quot;/g, '"');
              const systemStatusData = JSON.parse(cleanData);
              allSystemStatusData.push(...processSystemStatusData(systemStatusData));
            } catch (error) {
              console.error(`Error parsing system status for ATM ${atmid}:`, error);
            }
          }
        });

        setData([...allBillInventoryData, ...allSystemStatusData]);
      } catch (error) {
        console.error("Error fetching ATM data:", error);
      }
    }

    fetchATMData();
  }, []);

  useEffect(() => {
    if (activeTab === 2) {
      fetchLiveStatus();
    }
    // Interval removed here
  }, [activeTab]);
  
  useEffect(() => {
    if (selectedATM) {
      fetchHealthData();
    }
  }, [selectedATM, selectedDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedATM?.atmid]);

  const fetchLiveStatus = async () => {
    try {
      const response = await commonRunQuery(375, "");
      const records = response?.data?.body?.record;
      if (!records || !Array.isArray(records)) return;
  
      const formatted = records.map((item) => ({
        recid: item?.a?.recid,
        atmid: item?.a?.atmid,
        name: item?.a?.name,
        lastHeartbeat: item?.a?.lastheartbeattime || null,
        switchAlive: Number(item?.record?.switchalive),
      }));
      setLiveStatus(formatted);
    } catch (error) {
      console.error("Error fetching live status:", error);
    }
  };

  const fetchHealthData = async () => {
    if (!selectedATM) return;
    try {
      // Specific History
      const resHistory = await commonRunQuery(377, selectedATM.recid);
      const rawHistory = resHistory?.data?.body?.record;
      if (rawHistory) {
        const recordsHis = Array.isArray(rawHistory) ? rawHistory : [rawHistory];
        const formattedHistory = recordsHis.map((item) => ({
          recid: item.h?.recid,
          atmrecid: item.h?.atmrecid,
          name: item.a?.atm_name || selectedATM.name,
          device: item.h?.device || "Unknown Device",
          rectime: item.h?.rectime,
          corelatency: parseFloat(item.h?.corelatency || 0),
          switchlatency: parseFloat(item.h?.switchlatency || 0),
          failures: parseInt(item.h?.pastwritefailures || 0),
        }));
        setHeartbeatHistory(formattedHistory);
      }

      // B. Fetch Date-Specific History (Query 248) ONLY for the Chart
      const resChart = await commonRunQuery(379, `${selectedATM.recid}/${selectedDate}`);
      const rawChart = resChart?.data?.body?.record;
      if (rawChart) {
        const recordsChart = Array.isArray(rawChart) ? rawChart : [rawChart];
        const formattedChart = recordsChart.map((item) => ({
          // Map fields identical to your 377 structure
          rectime: item.h?.rectime,
          corelatency: parseFloat(item.h?.corelatency || 0),
          switchlatency: parseFloat(item.h?.switchlatency || 0),
          failures: parseInt(item.h?.pastwritefailures || 0),
        }));
        setChartHistory(formattedChart);
      } else {
        setChartHistory([]); // Clear if no data for that date
      }
  
      // Specific Status
      const resStatus = await commonRunQuery(378, selectedATM.atmid);
      const record = resStatus?.data?.body?.record;
      const finalRecord = Array.isArray(record) ? record[0] : record;
  
      if (finalRecord) {
        const rawJson = finalRecord?.atmstatus?.reportdata || "{}";
        const cleanJson = typeof rawJson === 'string' 
          ? JSON.parse(rawJson.replace(/&quot;/g, '"')) 
          : rawJson;
  
        setSpecificATMStatus({
          reportingtime: finalRecord.reportingtime,
          atmid: finalRecord.atmid,
          sensors: cleanJson.sensorStatus || {},
          supplies: cleanJson.supplyStatus || {},
          hardware: cleanJson.hardwareFitness || {},
        });
      }
    } catch (e) {
      console.error("Health Update Failed", e);
    }
  };

  function processResponseData(response) {
    const cassetteData = [];
    let i = 1;
    while (response[`cassette_${i}`]) {
      cassetteData.push({
        "Terminal ID": response.terminal_id,
        Cassette: response[`cassette_${i}`],
        Currency: response[`currency_${i}`],
        Denomination: response[`denomination_${i}`],
        "Bills Loaded": response[`bills_loaded_${i}`],
        "Bills Dispensed": response[`bills_dispensed_${i}`],
        "Bills Rejected": response[`bills_rejected_${i}`],
        "Bills Remaining": response[`bills_remaining_${i}`],
        "Value Loaded": response[`value_total_${i}`],
        "Value Dispensed": response[`value_dispensed_${i}`],
        "Value Rejected": response[`value_rejected_${i}`],
        "Value Remaining": response[`value_remaining_${i}`],
      });
      i++;
    }

    return [
      {
        id: response.terminal_id,
        name: `Terminal ID: ${response.terminal_id}`,
        details: `Card Acceptor Name: ${response.card_acceptor_name_loc}`,
        children: [
          {
            isTable: true,
            data: cassetteData,
          },
        ],
      },
    ];
  }

  function processSystemStatusData(systemStatusData) {
    function createDetailRows(data) {
      return Object.entries(data).map(([key, value]) => {
        const formattedKey = key
          .replace(/([A-Z])/g, " $1")
          .replace(/([0-9]+)/g, " $1")
          .replace(/_/g, " ")
          .trim()
          .replace(/\b\w/g, (char) => char.toUpperCase());
        return {
          id: key,
          name: formattedKey,
          details: value,
        };
      });
    }

    return [
      {
        id: systemStatusData.terminalId,
        name: `Terminal ID: ${systemStatusData.terminalId}`,
        details: `Card Acceptor Name: ${systemStatusData.cardAcceptorNameLoc}`,
        children: [
          {
            id: "sensor-status",
            name: "Sensor Status",
            details: "",
            children: createDetailRows(systemStatusData.sensorStatus),
          },
          {
            id: "supply-status",
            name: "Supply Status",
            details: "",
            children: createDetailRows(systemStatusData.supplyStatus),
          },
          {
            id: "hardware-fitness",
            name: "Hardware Status",
            details: "",
            children: createDetailRows(systemStatusData.hardwareFitness),
          },
        ],
      },
    ];
  }

  const getStatusColor = (status) => {
    if (status === 1) return "#27AE60"; // Green → ONLINE
    if (status === 0) return "#EB5757"; // Red → OFFLINE
    return "#F2C94C"; // Yellow → UNKNOWN
  };

  const getStatusText = (status) => {
    if (status === 1) return "ONLINE";
    if (status === 0) return "OFFLINE";
    return "UNKNOWN";
  };

  const renderTabContent = () => {
    const tableStyles = {
      backgroundColor: "#FFFFFF",
      color: "#333",
      fontWeight: "bold",
      padding: "15px",
      borderBottom: "1px solid #E0E0E0",
      textAlign: "center",
      fontFamily: "Nunito",
      fontSize: "16px",
    };

    const nameColumnStyle = {
      ...tableStyles,
      width: "20%", // Set a fixed width for the Name column
    };

    const detailsColumnStyle = {
      ...tableStyles,
      width: "80%", // Set a fixed width for the Details column
    };

    return (
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "0px 1px 15px rgba(0, 0, 0, 0.04)",
          borderRadius: "12px",
        }}
      >
        <Table
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            background: "#FFFFFF",
            marginTop: "10px",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={nameColumnStyle}>Name</TableCell>
              <TableCell sx={detailsColumnStyle}>Details</TableCell>
              <TableCell sx={tableStyles} />
            </TableRow>
          </TableHead>
          <TableBody>
            {activeTab === 0
              ? data
                  .filter(
                    (row) =>
                      row.children &&
                      row.children.some((child) => child.isTable)
                  )
                  .map((row, index) => (
                    <MUIStyledTableRow key={index} row={row} />
                  ))
              : data
                  .filter(
                    (row) =>
                      row.children &&
                      row.children.some((child) => !child.isTable)
                  )
                  .map((row, index) => (
                    <MUIStyledTableRow key={index} row={row} />
                  ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  console.log("HEART BEAT History----------",heartbeatHistory);

  const renderATMLiveStatus = () => {
    const handleManualRefresh = () => {
      if (selectedATM) {
        fetchHealthData(); // Refresh specific ATM details
      } else {
        fetchLiveStatus(); // Refresh the main list
      }
    };

    if (selectedATM) {
      return <ATMDetailView 
        atm={selectedATM} 
        onBack={() => setSelectedATM(null)}
        heartbeatHistory={heartbeatHistory}
        chartHistory={chartHistory}
        specificATMStatus={specificATMStatus}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        expandedPanel={expandedPanel}
        setExpandedPanel={setExpandedPanel}
        onRefresh={handleManualRefresh}
      />;
    }

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleManualRefresh}
          sx={{ color: "#00365A", borderColor: "#00365A" }}
        >
          Refresh Data
        </Button>
      </Box>

        {/* Conditional Rendering based on Sub-Tab */}
        {liveSubTab === "list" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {liveStatus.map((atm, index) => (
              <Paper
                key={index}
                onClick={() => {
                  console.log("atm-----------",atm);
                  setSelectedATM(atm);
                }} // Trigger the Detail View
                sx={{
                  padding: "12px",
                  borderRadius: "12px",
                  boxShadow: "0px 2px 10px rgba(0,0,0,0.06)",
                  cursor: "pointer",
                  transition: "transform 0.1s",
                  "&:active": { transform: "scale(0.98)", backgroundColor: "#fbfbfb" }
                }}
              >
                {/* Top Row */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <div style={{ fontWeight: 600, fontSize: "14px" }}>{atm.name}</div>
                    <div style={{ fontSize: "12px", color: "#828282" }}>{atm.atmid}</div>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: getStatusColor(atm.switchAlive),
                      color: "#fff",
                      padding: "4px 10px",
                      borderRadius: "20px",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    {getStatusText(atm.switchAlive)}
                  </Box>
                </Box>

                <Box sx={{ marginY: "10px", height: "1px", background: "#E0E0E0" }} />

                {/* Bottom Info */}
                <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#4F4F4F" }}>
                  <div>
                    <strong>Last HB</strong><br />
                    {atm.lastHeartbeat || "N/A"}
                  </div>
                  <div style={{ color: "#00365A", fontWeight: 600, alignSelf: "flex-end" }}>
                    Details →
                  </div>
                </Box>
              </Paper>
            ))}
          </Box>
        )}

        {/* {liveSubTab === "history" && renderHeartbeatHistory()}
        {liveSubTab === "global" && renderGlobalStatus()} */}
      </Box>
    );
  };

  // console.log("Active Tab----------", activeTab);
  console.log("liveSubTab----------", liveSubTab);

  return (
    <Box className="page-container">
      <Box className="header-container">
        <Box className="page-title">ATM Services</Box>
      </Box>
      <Tabs
        value={activeTab}
        onChange={(event, newValue) => setActiveTab(newValue)}
        sx={{
          "& .MuiTab-root": {
            textTransform: "none", // Keep the text in its original case
            color: "#BCBCBC", // Inactive tab color
            fontFamily: "Nunito",
            fontSize: 16, // Adjust font size to match
            fontWeight: 500, // Adjust font weight to match
            lineHeight: "24px", // Adjust line height to match
            padding: "12px 16px", // Adjust padding for better visual spacing
            minWidth: 0, // Override min-width to allow flexible tab width
            "&.Mui-selected": {
              color: "#00365A", // Active tab color
              fontWeight: 600, // Bold the active tab
              backgroundColor: "#FFFFFF", // White background for active tab
              borderTopLeftRadius: "8px", // Rounded corners for the active tab
              borderTopRightRadius: "8px",
              borderBottom: "4px solid #FAC800", // Yellow bottom border for active tab
            },
          },
          "& .MuiTabs-indicator": {
            display: "none", // Remove the default MUI tab indicator
          },
        }}
      >
        <Tab label="Bill Inventory" />
        <Tab label="System Status" />
        <Tab label="ATM Live Status" />
      </Tabs>
      <Box className="section-container">{(activeTab === 0 || activeTab === 1) ? renderTabContent() : renderATMLiveStatus()}</Box>
    </Box>
  );
};

export default ATMServices;
